import React, { useState } from 'react'
import {
  Brain, Send, CheckCircle, XCircle, AlertTriangle, Lightbulb,
  ArrowRight, History, ChevronDown, ChevronUp, Star, Target,
  BarChart2, BookOpen, RefreshCw, Award, ArrowLeftRight
} from 'lucide-react'
import MisconceptionAlert from '../components/MisconceptionAlert'
import OntologyTag from '../components/OntologyTag'
import { useProgressStore } from '../store/progressStore'
import type { TutorAnalysisResult } from '../types'

// ────────────────────────────────────────────────────────────────────────────
// 정수·유리수 단원 데모 시나리오 (NM01~NM13)
// ────────────────────────────────────────────────────────────────────────────
const DEMO_SCENARIOS = [
  {
    label: '📌 시나리오 1: 0을 양수로 오해 (NM01)',
    emoji: '📌',
    category: '양수·음수',
    step: 'NT01',
    question: '0은 양수인가요, 음수인가요?',
    answer: '양수입니다.',
    wrongAnswer: '양수입니다.',
    note: '0을 양수로 착각한 경우',
  },
  {
    label: '📌 시나리오 2: 절댓값이 음수 (NM03)',
    emoji: '📌',
    category: '절댓값',
    step: 'NT02',
    question: '|-7|의 값은 얼마인가요?',
    answer: '|-7| = -7',
    wrongAnswer: '|-7| = -7',
    note: '절댓값이 음수라고 계산한 경우',
  },
  {
    label: '📌 시나리오 3: 음수 대소 비교 오류 (NM05)',
    emoji: '📌',
    category: '대소 비교',
    step: 'NT03',
    question: '-9와 -3 중 어느 것이 더 큰가요?',
    answer: '-9가 더 큽니다. 왜냐하면 9 > 3이니까요.',
    wrongAnswer: '-9가 더 큽니다. 왜냐하면 9 > 3이니까요.',
    note: '음수끼리 대소 비교 시 절댓값 큰 쪽이 크다고 오해한 경우',
  },
  {
    label: '📌 시나리오 4: 뺄셈에서 부호 미변환 (NM07)',
    emoji: '📌',
    category: '정수 뺄셈',
    step: 'NT04',
    question: '(-4) - (-9)를 계산하시오.',
    answer: '(-4) - (-9) = -4 - 9 = -13',
    wrongAnswer: '(-4) - (-9) = -4 - 9 = -13',
    note: '뺄 수의 부호를 바꾸지 않은 경우',
  },
  {
    label: '📌 시나리오 5: 음수×음수=음수 (NM09)',
    emoji: '📌',
    category: '정수 곱셈',
    step: 'NT05',
    question: '(-5) × (-4)를 계산하시오.',
    answer: '(-5) × (-4) = -20',
    wrongAnswer: '(-5) × (-4) = -20',
    note: '음수×음수를 음수로 계산한 경우',
  },
  {
    label: '✅ 시나리오 6: 올바른 혼합 계산',
    emoji: '✅',
    category: '사칙연산 혼합',
    step: 'NT07',
    question: '3 + (-2) × 4를 계산하시오.',
    answer: '3 + (-2) × 4 = 3 + (-8) = -5',
    wrongAnswer: '3 + (-2) × 4 = 3 + (-2) × 4 = 1 × 4 = 4',
    note: '정확한 답변',
  },
]

// 시나리오별 결과 매핑 키
const SCENARIO_KEYS = [
  'NT01-wrong-nm01', 'NT02-wrong-nm03', 'NT03-wrong-nm05',
  'NT04-wrong-nm07', 'NT05-wrong-nm09', 'NT07-correct',
]

// ────────────────────────────────────────────────────────────────────────────
// 정수·유리수 단원 데모 결과 데이터
// ────────────────────────────────────────────────────────────────────────────
const DEMO_RESULTS: Record<string, TutorAnalysisResult> = {
  'NT01-wrong-nm01': {
    student_id: 'student_001', current_step: 'NT01', step_concept: '양수·음수·정수의 개념', score: 20,
    mastery: { step_id: 'NT01', score: 20, attempts: 1, mastered: false, near_mastery: false, level: 'learning', message: '0의 분류를 다시 학습해야 합니다.' },
    misconceptions: [{
      id: 'NM01', type: 'ZERO_IS_POSITIVE', severity: 'high',
      description: 'Treating 0 as a positive number',
      description_ko: '0을 양수로 잘못 분류하는 오류',
      feedback_id: 'F-NM01', learning_step: 'NT01',
    }],
    concept_gaps: [{ concept: 'Integer', from_misconception: 'NM01', severity: 'high', remedial_step: 'NT01' }],
    feedbacks: [{
      id: 'F-NM01', misconception: 'NM01',
      title: '0은 양수도 음수도 아닙니다!',
      message: '정수는 양의 정수(+1, +2, ...), 0, 음의 정수(-1, -2, ...)로 분류됩니다. 0은 기준점(원점)으로 어느 쪽에도 속하지 않는 특별한 수입니다. 수직선에서 가운데가 0이에요.',
      hint: '정수 분류: 양의 정수 | 0 | 음의 정수. 0은 중립! 양수도 음수도 아님.',
      next_step: 'NT01 정수 개념 재학습',
    }],
    primary_feedback: {
      id: 'F-NM01', misconception: 'NM01',
      title: '0은 양수도 음수도 아닙니다!',
      message: '수직선에서 0은 기준점(원점)입니다. 양수는 0보다 큰 수(오른쪽), 음수는 0보다 작은 수(왼쪽)입니다. 0 자체는 어디에도 속하지 않아요.',
      hint: '0 = 기준점 (중립), 양의 정수 > 0 > 음의 정수',
      next_step: 'NT01 정수 개념 재학습',
    },
    next_step_recommendation: {
      current_step: 'NT01', next_step: 'NT01',
      recommendation: 'reinforce', reason: 'NM01 오개념 해결 후 정수 분류 재학습 필요', score: 20, misconception_count: 1,
    },
    ontology_classes_activated: ['Integer', 'PositiveInteger', 'NegativeInteger', 'Zero', 'Misconception', 'Feedback'],
    agent_reasoning: {
      perceived: '학생이 0을 양수로 분류했습니다.',
      reasoned: 'NM01(0을 양수로 오인) 오개념이 감지됩니다. 정수의 분류 기준을 모르거나 혼동하고 있습니다.',
      planned: 'NM01 피드백으로 0의 특별한 위치를 수직선으로 설명',
      acted: '수직선에서 0의 위치(기준점)를 시각적으로 이해시킵니다.',
    },
  },

  'NT02-wrong-nm03': {
    student_id: 'student_001', current_step: 'NT02', step_concept: '수직선과 절댓값', score: 15,
    mastery: { step_id: 'NT02', score: 15, attempts: 1, mastered: false, near_mastery: false, level: 'learning', message: '절댓값은 항상 0 이상입니다.' },
    misconceptions: [{
      id: 'NM03', type: 'ABSOLUTE_VALUE_NEGATIVE', severity: 'high',
      description: 'Thinking absolute value can be negative',
      description_ko: '절댓값이 음수가 될 수 있다는 오해',
      feedback_id: 'F-NM03', learning_step: 'NT02',
    }],
    concept_gaps: [{ concept: 'AbsoluteValue', from_misconception: 'NM03', severity: 'high', remedial_step: 'NT02' }],
    feedbacks: [{
      id: 'F-NM03', misconception: 'NM03',
      title: '절댓값은 항상 0 이상입니다!',
      message: '절댓값은 수직선에서 0까지의 "거리"를 나타냅니다. 거리는 절대 음수가 될 수 없어요! |-7| = 7, |-3/2| = 3/2입니다. 절댓값 기호 | | 안의 수에서 부호만 제거하면 됩니다.',
      hint: '절댓값 = 부호 제거 (항상 0 이상). |-7| = 7, |+5| = 5, |0| = 0',
      next_step: 'NT02 절댓값 개념 재학습',
    }],
    primary_feedback: {
      id: 'F-NM03', misconception: 'NM03',
      title: '|-7| = 7, not -7!',
      message: '절댓값은 원점(0)에서의 거리입니다. -7은 0에서 7만큼 떨어져 있으므로 |-7| = 7. 부호와 관계없이 항상 양수(또는 0)입니다.',
      hint: '절댓값 = 거리 → 항상 양수. |-7| = 7, |7| = 7',
      next_step: 'NT02 재학습',
    },
    next_step_recommendation: {
      current_step: 'NT02', next_step: 'NT02',
      recommendation: 'reinforce', reason: 'NM03 오개념 해결 후 절댓값 개념 재학습', score: 15, misconception_count: 1,
    },
    ontology_classes_activated: ['AbsoluteValue', 'NumberLine', 'Distance', 'Misconception', 'Feedback'],
    agent_reasoning: {
      perceived: '학생이 |-7| = -7이라고 답했습니다.',
      reasoned: 'NM03(절댓값이 음수라는 오해) 오개념이 강하게 감지됩니다.',
      planned: 'NM03 피드백으로 절댓값=거리 개념을 수직선으로 시각화',
      acted: '수직선에서 -7이 0에서 7칸 떨어져 있다는 시각적 설명으로 이해시킵니다.',
    },
  },

  'NT03-wrong-nm05': {
    student_id: 'student_001', current_step: 'NT03', step_concept: '정수의 대소 비교', score: 20,
    mastery: { step_id: 'NT03', score: 20, attempts: 1, mastered: false, near_mastery: false, level: 'learning', message: '음수끼리 대소 비교 규칙을 다시 학습하세요.' },
    misconceptions: [{
      id: 'NM05', type: 'NEGATIVE_COMPARISON_ERROR', severity: 'high',
      description: 'Thinking larger absolute value means larger negative number',
      description_ko: '음수끼리 비교 시 절댓값 큰 쪽이 더 크다는 오해',
      feedback_id: 'F-NM05', learning_step: 'NT03',
    }],
    concept_gaps: [{ concept: 'IntegerOrder', from_misconception: 'NM05', severity: 'high', remedial_step: 'NT03' }],
    feedbacks: [{
      id: 'F-NM05', misconception: 'NM05',
      title: '음수는 절댓값이 작을수록 큰 수!',
      message: '수직선에서 오른쪽에 있는 수가 더 큰 수입니다. -3은 -9보다 오른쪽에 있으므로 -3 > -9입니다. 음수끼리 비교할 때는 절댓값이 작은 쪽이 더 큰 수예요!',
      hint: '수직선: ← -9 ··· -3 ··· 0 → 오른쪽 = 큰 수. -3 > -9',
      next_step: 'NT03 대소 비교 재학습',
    }],
    primary_feedback: {
      id: 'F-NM05', misconception: 'NM05',
      title: '-3 > -9 (수직선 오른쪽이 더 크다!)',
      message: '-9와 -3을 수직선에 표시하면: ← -9 ··· -3 ··· 0 →. -3이 -9보다 오른쪽 → -3 > -9. 음수는 0에서 멀어질수록(절댓값이 커질수록) 더 작은 수입니다.',
      hint: '음수 대소: 절댓값이 작을수록 큰 수. -1 > -2 > -10',
      next_step: 'NT03 재학습',
    },
    next_step_recommendation: {
      current_step: 'NT03', next_step: 'NT03',
      recommendation: 'reinforce', reason: 'NM05 오개념 해결 후 수직선 대소 비교 재학습', score: 20, misconception_count: 1,
    },
    ontology_classes_activated: ['IntegerOrder', 'NumberLine', 'AbsoluteValue', 'NegativeInteger', 'Misconception', 'Feedback'],
    agent_reasoning: {
      perceived: '학생이 -9 > -3이라고 답했습니다.',
      reasoned: 'NM05(음수 대소비교 오류) 오개념이 감지됩니다. 절댓값 크기를 수의 크기로 혼동하고 있습니다.',
      planned: 'NM05 피드백으로 수직선 오른쪽=큰 수 원칙을 음수 영역에 적용',
      acted: '수직선 시각화로 -3이 -9보다 오른쪽임을 직관적으로 이해시킵니다.',
    },
  },

  'NT04-wrong-nm07': {
    student_id: 'student_001', current_step: 'NT04', step_concept: '정수·유리수의 덧셈·뺄셈', score: 20,
    mastery: { step_id: 'NT04', score: 20, attempts: 1, mastered: false, near_mastery: false, level: 'learning', message: '뺄셈을 덧셈으로 변환하는 규칙을 다시 학습하세요.' },
    misconceptions: [{
      id: 'NM07', type: 'SUBTRACTION_SIGN_ERROR', severity: 'high',
      description: 'Not flipping the sign when converting subtraction to addition',
      description_ko: '뺄셈에서 부호를 바꾸지 않는 오류',
      feedback_id: 'F-NM07', learning_step: 'NT04',
    }],
    concept_gaps: [{ concept: 'IntegerSubtraction', from_misconception: 'NM07', severity: 'high', remedial_step: 'NT04' }],
    feedbacks: [{
      id: 'F-NM07', misconception: 'NM07',
      title: '뺄셈 = 뺄 수의 부호를 바꿔서 덧셈!',
      message: '정수의 뺄셈: a - b = a + (-b). 뺄 수의 부호를 반대로 바꾸고 덧셈을 합니다. (-4) - (-9) = (-4) + (+9) = +5. -(-9)에서 음수의 음수는 양수가 됩니다!',
      hint: 'a - b = a + (-b). 부호 반대로 → 덧셈. (-4)-(-9)=(-4)+(+9)=+5',
      next_step: 'NT04 뺄셈 규칙 재학습',
    }],
    primary_feedback: {
      id: 'F-NM07', misconception: 'NM07',
      title: '(-4) - (-9) = +5, not -13!',
      message: '(-4) - (-9): 뺄 수 (-9)의 부호를 반대로 → (+9). 그러면 (-4) + (+9) = +5. -(-9)는 +9입니다. 부호 두 개가 붙으면 양수가 됩니다!',
      hint: '-(-9) = +9, -(-3) = +3. 음음=양!',
      next_step: 'NT04 재학습',
    },
    next_step_recommendation: {
      current_step: 'NT04', next_step: 'NT04',
      recommendation: 'reinforce', reason: 'NM07 오개념 해결 후 뺄셈→덧셈 변환 재학습', score: 20, misconception_count: 1,
    },
    ontology_classes_activated: ['IntegerSubtraction', 'IntegerAddition', 'SignRule', 'Misconception', 'Feedback'],
    agent_reasoning: {
      perceived: '학생이 (-4)-(-9)=-13이라고 답했습니다.',
      reasoned: 'NM07(뺄셈에서 부호 미변환) 오개념이 감지됩니다. (-9)의 부호를 바꾸지 않고 그대로 뺐습니다.',
      planned: 'NM07 피드백으로 뺄셈=부호반전+덧셈 규칙을 단계별로 설명',
      acted: '(-4)+( +9)=+5 과정을 단계별로 명확히 보여줍니다.',
    },
  },

  'NT05-wrong-nm09': {
    student_id: 'student_001', current_step: 'NT05', step_concept: '정수·유리수의 곱셈·나눗셈', score: 15,
    mastery: { step_id: 'NT05', score: 15, attempts: 1, mastered: false, near_mastery: false, level: 'learning', message: '부호 규칙(같은 부호→양수)을 다시 학습하세요.' },
    misconceptions: [{
      id: 'NM09', type: 'NEGATIVE_TIMES_NEGATIVE_ERROR', severity: 'high',
      description: 'Thinking negative × negative = negative',
      description_ko: '음수×음수=음수라는 오류',
      feedback_id: 'F-NM09', learning_step: 'NT05',
    }],
    concept_gaps: [{ concept: 'IntegerMultiplication', from_misconception: 'NM09', severity: 'high', remedial_step: 'NT05' }],
    feedbacks: [{
      id: 'F-NM09', misconception: 'NM09',
      title: '(-) × (-) = (+) 같은 부호는 항상 양수!',
      message: '정수 곱셈 부호 규칙: 같은 부호끼리 → (+), 다른 부호끼리 → (-). (+)×(+)=+, (-)×(-)=+, (+)×(-)=-, (-)×(+)=-. (-5)×(-4) = +(5×4) = +20입니다.',
      hint: '같은 부호 → 양수. (-5)×(-4)=+20, (-3)×(-2)=+6',
      next_step: 'NT05 곱셈 부호 규칙 재학습',
    }],
    primary_feedback: {
      id: 'F-NM09', misconception: 'NM09',
      title: '(-5)×(-4) = +20, not -20!',
      message: '(-5)×(-4): 두 수 모두 음수 → 같은 부호 → 결과는 양수. 절댓값 곱: 5×4=20. 최종 답: +20. 음수×음수=양수는 수학의 핵심 규칙입니다!',
      hint: '부호규칙: (-)×(-)=(+), (-)×(+)=(-)',
      next_step: 'NT05 재학습',
    },
    next_step_recommendation: {
      current_step: 'NT05', next_step: 'NT05',
      recommendation: 'reinforce', reason: 'NM09 오개념 해결 후 곱셈 부호 규칙 재학습', score: 15, misconception_count: 1,
    },
    ontology_classes_activated: ['IntegerMultiplication', 'SignRule', 'AbsoluteValue', 'Misconception', 'Feedback'],
    agent_reasoning: {
      perceived: '학생이 (-5)×(-4)=-20이라고 답했습니다.',
      reasoned: 'NM09(음수×음수=음수 오류) 오개념이 강하게 감지됩니다.',
      planned: 'NM09 피드백으로 같은부호→양수 규칙을 표로 정리하여 설명',
      acted: '부호 규칙 표와 구체적 계산 과정으로 양수 결과를 납득시킵니다.',
    },
  },

  'NT07-correct': {
    student_id: 'student_001', current_step: 'NT07', step_concept: '사칙연산 혼합 계산', score: 100,
    mastery: { step_id: 'NT07', score: 100, attempts: 1, mastered: true, near_mastery: false, level: 'mastered', message: '완벽합니다! 혼합 계산을 마스터했습니다.' },
    misconceptions: [],
    concept_gaps: [],
    feedbacks: [{
      id: 'F-correct', type: 'encouragement',
      title: '훌륭해요! 🎉',
      message: '3 + (-2) × 4에서 곱셈을 먼저 계산하는 순서를 완벽히 이해했습니다! 곱셈·나눗셈 → 덧셈·뺄셈 순서를 잘 적용했어요.',
      emoji: '🌟',
      personalized_message: '완벽한 답변입니다! 정수·유리수 단원 다음 단계로 도전해보세요.',
    }],
    primary_feedback: {
      id: 'F-correct', type: 'encouragement',
      title: '훌륭해요! 🎉',
      message: '3 + (-2) × 4 = 3 + (-8) = -5를 정확히 계산했습니다! 계산 순서(곱셈 먼저)를 완벽하게 적용했어요.',
      emoji: '🌟',
    },
    next_step_recommendation: {
      current_step: 'NT07', next_step: 'NT08',
      recommendation: 'advance', reason: '혼합 계산 마스터! 심화 학습으로 진행', score: 100, misconception_count: 0,
    },
    ontology_classes_activated: ['MixedOperation', 'OperationOrder', 'IntegerArithmetic', 'Feedback'],
    agent_reasoning: {
      perceived: '학생이 3+(-2)×4=-5를 정확하게 계산했습니다.',
      reasoned: '오개념이 없습니다. 완전 학습 상태입니다.',
      planned: '격려 피드백 후 NT08 심화 학습으로 진행',
      acted: '칭찬과 함께 다음 단계를 안내합니다.',
    },
  },
}

// ────────────────────────────────────────────────────────────────────────────
// 세션 히스토리 타입
// ────────────────────────────────────────────────────────────────────────────
interface HistoryEntry {
  scenarioLabel: string
  score: number
  grade: string
  misconceptionCount: number
  at: string
}

function getGradeFromScore(score: number): string {
  if (score >= 90) return 'A'
  if (score >= 70) return 'B'
  if (score >= 50) return 'C'
  return 'D'
}

// 단계별 색상 매핑
const STEP_COLORS: Record<string, string> = {
  NT01: 'bg-blue-100 text-blue-700',
  NT02: 'bg-purple-100 text-purple-700',
  NT03: 'bg-green-100 text-green-700',
  NT04: 'bg-orange-100 text-orange-700',
  NT05: 'bg-red-100 text-red-700',
  NT06: 'bg-teal-100 text-teal-700',
  NT07: 'bg-indigo-100 text-indigo-700',
  NT08: 'bg-pink-100 text-pink-700',
}

// ────────────────────────────────────────────────────────────────────────────
// 컴포넌트
// ────────────────────────────────────────────────────────────────────────────
export default function N2TutorPage() {
  const [selectedIdx, setSelectedIdx] = useState(0)
  const [customAnswer, setCustomAnswer] = useState('')
  const [result, setResult] = useState<TutorAnalysisResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [history, setHistory] = useState<HistoryEntry[]>([])
  const [showHistory, setShowHistory] = useState(false)
  const [showReasoning, setShowReasoning] = useState(false)
  const [showOntology, setShowOntology] = useState(false)

  const { recordMisconceptions } = useProgressStore(s => ({
    recordMisconceptions: s.recordMisconceptions,
  }))

  const scenario = DEMO_SCENARIOS[selectedIdx]

  const handleAnalyze = async () => {
    setLoading(true)
    setResult(null)
    await new Promise(r => setTimeout(r, 900))
    const key = SCENARIO_KEYS[selectedIdx]
    const res = DEMO_RESULTS[key]
    setResult(res)

    // 오개념 progressStore에 기록
    if (res.misconceptions.length > 0) {
      recordMisconceptions('N1-2', res.misconceptions.map(m => m.id))
    }

    // 세션 히스토리 추가
    setHistory(prev => [
      {
        scenarioLabel: scenario.label,
        score: res.score,
        grade: getGradeFromScore(res.score),
        misconceptionCount: res.misconceptions.length,
        at: new Date().toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' }),
      },
      ...prev,
    ].slice(0, 10))

    setLoading(false)
  }

  const recColor =
    result?.next_step_recommendation.recommendation === 'advance'
      ? 'text-emerald-700 bg-emerald-50 border border-emerald-200'
      : result?.next_step_recommendation.recommendation === 'reinforce'
        ? 'text-blue-700 bg-blue-50 border border-blue-200'
        : 'text-orange-700 bg-orange-50 border border-orange-200'

  const avgScore = history.length > 0
    ? Math.round(history.reduce((s, h) => s + h.score, 0) / history.length)
    : null

  return (
    <div className="p-6 space-y-5">
      {/* ── 헤더 ──────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
            <Brain size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
              AI 튜터 — 정수와 유리수
              <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-semibold">N1-2</span>
            </h1>
            <p className="text-sm text-slate-500">정수·유리수 단원의 오개념을 감지하고 맞춤 피드백을 제공합니다</p>
          </div>
        </div>
        {avgScore !== null && (
          <div className="flex items-center gap-2 bg-indigo-50 border border-indigo-200 px-3 py-1.5 rounded-xl">
            <Star size={13} className="text-indigo-500" />
            <span className="text-xs font-bold text-indigo-700">세션 평균 {avgScore}점</span>
          </div>
        )}
      </div>

      {/* ── 단원 개요 배너 ─────────────────────────────────────────────── */}
      <div className="rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-700 text-white p-4">
        <div className="flex items-center gap-2 mb-2">
          <ArrowLeftRight size={16} />
          <span className="font-bold text-sm">정수·유리수 단원 주요 오개념</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {[
            { code: 'NM01', label: '0≠양수', step: 'NT01' },
            { code: 'NM03', label: '절댓값≥0', step: 'NT02' },
            { code: 'NM05', label: '음수대소비교', step: 'NT03' },
            { code: 'NM07', label: '뺄셈=부호반전', step: 'NT04' },
            { code: 'NM09', label: '(-)×(-)=(+)', step: 'NT05' },
            { code: 'NM13', label: '혼합계산순서', step: 'NT07' },
          ].map(m => (
            <span key={m.code} className="text-xs bg-white/20 px-2 py-1 rounded-lg font-medium">
              {m.code}: {m.label}
            </span>
          ))}
        </div>
      </div>

      {/* ── 세션 기록 토글 ──────────────────────────────────────────────── */}
      {history.length > 0 && (
        <div className="card border border-slate-200">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="flex items-center justify-between w-full text-sm font-semibold text-slate-700 hover:text-indigo-600 transition-colors"
          >
            <span className="flex items-center gap-2">
              <History size={14} />
              세션 기록 ({history.length}회)
            </span>
            {showHistory ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          {showHistory && (
            <div className="mt-3 space-y-1.5">
              {history.map((h, i) => (
                <div key={i} className="flex items-center gap-3 text-xs py-1 border-b border-slate-50 last:border-0">
                  <span className="text-slate-400 w-10">{h.at}</span>
                  <span className="flex-1 text-slate-600 truncate">{h.scenarioLabel.replace(/^📌|✅\s/, '')}</span>
                  <span className={`font-black w-12 text-right ${h.score >= 70 ? 'text-emerald-600' : h.score >= 40 ? 'text-orange-600' : 'text-red-600'}`}>
                    {h.score}점
                  </span>
                  <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${h.score >= 70 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                    {h.grade}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── 2열 레이아웃: 입력 | 결과 ─────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ── 왼쪽: 시나리오 선택 + 답안 입력 ───────────────────────────── */}
        <div className="space-y-4">
          {/* 시나리오 목록 */}
          <div className="card">
            <p className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-1.5">
              <Target size={14} className="text-indigo-500" />
              정수·유리수 오개념 시나리오
              <span className="ml-auto text-xs text-slate-400 font-normal">({DEMO_SCENARIOS.length}가지)</span>
            </p>
            <div className="space-y-1.5">
              {DEMO_SCENARIOS.map((s, i) => (
                <button
                  key={i}
                  onClick={() => { setSelectedIdx(i); setResult(null); setCustomAnswer('') }}
                  className={`w-full text-left px-3 py-2.5 rounded-xl text-xs transition-all flex items-start gap-2 ${
                    selectedIdx === i
                      ? 'bg-indigo-100 text-indigo-700 font-semibold shadow-sm'
                      : 'hover:bg-slate-50 text-slate-600'
                  }`}
                >
                  <span className="text-base leading-none mt-0.5">{s.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold">{s.category}</div>
                    <div className="text-slate-400 font-normal mt-0.5 truncate">{s.note}</div>
                  </div>
                  <span className={`flex-shrink-0 text-xs px-1.5 py-0.5 rounded-md font-bold ${STEP_COLORS[s.step] || 'bg-slate-100 text-slate-600'}`}>
                    {s.step}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* 문제 + 답안 입력 */}
          <div className="card space-y-3">
            <div>
              <p className="text-xs font-semibold text-slate-500 mb-1 flex items-center gap-1">
                <BookOpen size={11} />
                문제
                <span className={`ml-1 text-xs px-1.5 py-0.5 rounded-md font-bold ${STEP_COLORS[scenario.step] || 'bg-slate-100 text-slate-600'}`}>
                  {scenario.step}
                </span>
              </p>
              <p className="text-sm text-slate-800 bg-slate-50 rounded-xl p-3 border border-slate-200 leading-relaxed">
                {scenario.question}
              </p>
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-500 mb-1">학생 답안 (수정 가능)</p>
              <textarea
                className="w-full h-20 resize-none border border-slate-200 rounded-xl p-3 text-sm text-slate-800 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent transition-all"
                value={customAnswer || scenario.wrongAnswer}
                onChange={e => setCustomAnswer(e.target.value)}
                placeholder="답안을 입력하세요..."
              />
              <p className="text-xs text-slate-400 mt-1">
                💡 답안을 직접 수정해보면 다양한 오개념을 실험할 수 있습니다.
              </p>
            </div>
            <button
              onClick={handleAnalyze}
              disabled={loading}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2.5 rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? (
                <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> AI 분석 중...</>
              ) : (
                <><Send size={16} /> AI 튜터 분석 시작</>
              )}
            </button>
          </div>
        </div>

        {/* ── 오른쪽: 분석 결과 ─────────────────────────────────────────── */}
        <div className="space-y-4">
          {!result && !loading && (
            <div className="card flex flex-col items-center justify-center h-80 text-center bg-gradient-to-b from-indigo-50 to-white">
              <div className="w-16 h-16 rounded-2xl bg-indigo-100 flex items-center justify-center mb-4">
                <ArrowLeftRight size={32} className="text-indigo-400" />
              </div>
              <p className="text-slate-500 text-sm font-semibold">정수·유리수 시나리오를 선택하고</p>
              <p className="text-slate-400 text-sm">"AI 튜터 분석 시작"을 눌러보세요</p>
              <div className="mt-4 text-xs text-slate-300">
                NM01~NM13 오개념 감지 · 단계별 피드백 · 다음 학습 추천
              </div>
            </div>
          )}

          {loading && (
            <div className="card flex flex-col items-center justify-center h-80 text-center">
              <div className="relative mb-4">
                <div className="w-16 h-16 border-4 border-indigo-100 border-t-indigo-500 rounded-full animate-spin" />
                <Brain size={20} className="absolute inset-0 m-auto text-indigo-400" />
              </div>
              <p className="text-slate-600 font-semibold">AI 분석 중...</p>
              <p className="text-slate-400 text-xs mt-1">정수·유리수 오개념 패턴 매칭 중...</p>
            </div>
          )}

          {result && (
            <>
              {/* 점수 카드 */}
              <div className={`card flex items-center gap-4 ${result.score >= 70 ? 'bg-emerald-50 border border-emerald-200' : result.score >= 40 ? 'bg-orange-50 border border-orange-200' : 'bg-red-50 border border-red-200'}`}>
                {result.score >= 70
                  ? <CheckCircle size={36} className="text-emerald-500 flex-shrink-0" />
                  : <XCircle size={36} className="text-red-500 flex-shrink-0" />}
                <div className="flex-1">
                  <div className="text-3xl font-black text-slate-800">{result.score}점</div>
                  <div className="text-sm text-slate-600 mt-0.5">{result.mastery.message}</div>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                      result.mastery.mastered ? 'bg-emerald-100 text-emerald-700' :
                      result.mastery.near_mastery ? 'bg-blue-100 text-blue-700' :
                      'bg-slate-100 text-slate-600'
                    }`}>
                      {result.mastery.level === 'mastered' ? '✓ 마스터' :
                       result.mastery.level === 'near_mastery' ? '거의 완료' : '학습 중'}
                    </span>
                    <span className={`text-xs px-1.5 py-0.5 rounded-md font-bold ${STEP_COLORS[result.current_step] || 'bg-slate-100 text-slate-600'}`}>
                      {result.current_step}
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <div className={`text-2xl font-black ${result.score >= 70 ? 'text-emerald-500' : result.score >= 40 ? 'text-orange-500' : 'text-red-500'}`}>
                    {getGradeFromScore(result.score)}
                  </div>
                  <div className="text-xs text-slate-400">등급</div>
                </div>
              </div>

              {/* 오개념 */}
              {result.misconceptions.length > 0 && (
                <div className="card">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertTriangle size={15} className="text-orange-500" />
                    <p className="text-sm font-bold text-slate-700">감지된 오개념</p>
                    <span className="ml-auto text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-bold">
                      {result.misconceptions.length}건
                    </span>
                  </div>
                  <div className="space-y-3">
                    {result.misconceptions.map(m => (
                      <MisconceptionAlert
                        key={m.id}
                        misconception={m}
                        feedback={result.feedbacks.find(f => f.id === m.feedback_id)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* 주요 피드백 */}
              {result.primary_feedback && (
                <div className="card bg-amber-50 border border-amber-200">
                  <div className="flex items-start gap-2">
                    <Lightbulb size={16} className="text-amber-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-bold text-slate-800">{result.primary_feedback.title}</p>
                      <p className="text-sm text-slate-700 mt-1 leading-relaxed">{result.primary_feedback.message}</p>
                      {result.primary_feedback.hint && (
                        <div className="mt-2 text-xs text-amber-800 bg-amber-100 rounded-xl px-3 py-2 border border-amber-200">
                          💡 <span className="font-semibold">힌트:</span> {result.primary_feedback.hint}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* 다음 학습 추천 */}
              <div className={`card flex items-center gap-3 ${recColor}`}>
                <ArrowRight size={18} className="flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs font-semibold uppercase tracking-wide opacity-70">다음 학습 단계 추천</p>
                  <p className="text-sm font-bold">{result.next_step_recommendation.next_step}</p>
                  <p className="text-xs opacity-80 mt-0.5">{result.next_step_recommendation.reason}</p>
                </div>
                <span className={`text-xs px-2 py-1 rounded-lg font-bold ${
                  result.next_step_recommendation.recommendation === 'advance'
                    ? 'bg-emerald-200 text-emerald-800'
                    : 'bg-blue-200 text-blue-800'
                }`}>
                  {result.next_step_recommendation.recommendation === 'advance' ? '↑ 진행' : '↩ 복습'}
                </span>
              </div>

              {/* Agentic AI 추론 과정 (토글) */}
              <div className="card">
                <button
                  onClick={() => setShowReasoning(!showReasoning)}
                  className="flex items-center justify-between w-full text-sm font-semibold text-slate-700 hover:text-indigo-600 transition-colors"
                >
                  <span className="flex items-center gap-2">
                    <BarChart2 size={14} />
                    🤖 Agentic AI 추론 과정
                  </span>
                  {showReasoning ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                </button>
                {showReasoning && (
                  <div className="mt-3 space-y-2 text-xs text-slate-600">
                    {[
                      { label: '인식', value: result.agent_reasoning.perceived },
                      { label: '추론', value: result.agent_reasoning.reasoned },
                      { label: '계획', value: result.agent_reasoning.planned },
                      { label: '행동', value: result.agent_reasoning.acted },
                    ].map(({ label, value }) => (
                      <div key={label} className="flex gap-2">
                        <span className="font-bold text-slate-500 w-8 flex-shrink-0">{label}</span>
                        <span className="text-slate-600">{value}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* OWL 클래스 (토글) */}
              <div className="card">
                <button
                  onClick={() => setShowOntology(!showOntology)}
                  className="flex items-center justify-between w-full text-sm font-semibold text-slate-700 hover:text-indigo-600 transition-colors"
                >
                  <span className="flex items-center gap-2">
                    <Award size={14} />
                    활성화된 OWL 클래스
                  </span>
                  {showOntology ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                </button>
                {showOntology && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {result.ontology_classes_activated.map(c => (
                      <OntologyTag key={c} className_name={c} />
                    ))}
                  </div>
                )}
              </div>

              {/* 다시 시도 버튼 */}
              <button
                onClick={() => { setResult(null); setCustomAnswer('') }}
                className="w-full flex items-center justify-center gap-2 text-sm text-slate-500 hover:text-indigo-600 border border-slate-200 rounded-xl py-2 hover:border-indigo-300 transition-colors"
              >
                <RefreshCw size={13} /> 다른 시나리오 선택
              </button>
            </>
          )}
        </div>
      </div>

      {/* ── 오개념 빠른 참조 카드 ─────────────────────────────────────── */}
      <div className="card">
        <h3 className="text-sm font-bold text-slate-700 mb-4 flex items-center gap-2">
          <Star size={15} className="text-amber-500" />
          정수·유리수 핵심 오개념 빠른 참조
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {[
            { code: 'NM01', title: '0은 양수가 아님', fix: '0 = 기준점 (양수도 음수도 아님)', icon: '0️⃣' },
            { code: 'NM03', title: '절댓값은 항상 0 이상', fix: '|-7|=7 (거리=항상 0 이상)', icon: '📏' },
            { code: 'NM05', title: '음수: 절댓값 작을수록 큼', fix: '-3 > -9 (수직선 오른쪽)', icon: '↔️' },
            { code: 'NM07', title: '뺄셈→덧셈 변환 필수', fix: 'a-b = a+(-b), -(-9)=+9', icon: '➖' },
            { code: 'NM09', title: '(-) × (-) = +', fix: '같은 부호→양수, 다른 부호→음수', icon: '✖️' },
            { code: 'NM13', title: '곱셈·나눗셈 먼저', fix: '3+(-2)×4: 곱 먼저(-8), 덧셈(-5)', icon: '🔢' },
          ].map(m => (
            <div key={m.code} className="bg-slate-50 rounded-xl p-3 border border-slate-200">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-lg">{m.icon}</span>
                <span className="text-xs font-mono font-bold text-indigo-600">{m.code}</span>
              </div>
              <p className="text-xs font-bold text-slate-700 mb-1">{m.title}</p>
              <p className="text-xs text-slate-500 font-mono">{m.fix}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
